/**
 * Enhanced Feature Stacking Animation with GSAP ScrollTrigger
 * Medium Impact Style - BenQ-inspired with scale, rotation, and depth
 *
 * @package DailyHydrogen
 * @since 2.0.0
 */

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger);

class FeatureStacking {
  constructor() {
    this.container = document.querySelector('.features-scroll-container');
    this.cards = null;
    this.isDesktop = window.innerWidth >= 1024;
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (this.prefersReducedMotion) {
      console.log('[FeatureStacking] Reduced motion preference detected - disabling animations');
      return;
    }

    if (!this.container) {
      console.log('[FeatureStacking] Container not found');
      return;
    }

    this.init();
  }

  init() {
    // Get all feature cards
    this.cards = gsap.utils.toArray('.feature-deep-dive-section');

    if (this.cards.length === 0) {
      console.log('[FeatureStacking] No cards found');
      return;
    }

    console.log(`[FeatureStacking] Animation disabled - static layout`);

    // No animations - just static layout
    // Animations have been disabled per user request
  }

  initDesktopStacking() {
    console.log('[FeatureStacking] Card stack animation (Simple stacking)');

    this.cards.forEach((card, index) => {
      const isLast = index === this.cards.length - 1;

      // Set z-index: higher cards on top
      gsap.set(card, { zIndex: this.cards.length - index });

      if (!isLast) {
        // Create ScrollTrigger for each card
        ScrollTrigger.create({
          trigger: card,
          start: 'top top',
          end: 'bottom top',
          pin: true,
          pinSpacing: true,
          scrub: 1,
          id: `stack-${index}`,
          // markers: true, // Debug
          onUpdate: (self) => {
            const progress = self.progress;

            // Subtle scale and fade as card scrolls away
            const scale = 1 - (progress * 0.05); // 1.0 -> 0.95 (more subtle)
            const opacity = 1 - (progress * 0.3); // 1.0 -> 0.7 (less fade)

            gsap.to(card, {
              scale: scale,
              opacity: opacity,
              duration: 0.1,
              ease: 'none'
            });
          }
        });
      }
    });

    ScrollTrigger.refresh();
    console.log('[FeatureStacking] Desktop stacking initialized successfully');
  }

  initMobileAnimations() {
    console.log('[FeatureStacking] Mobile animations enabled');

    // Simpler fade-in animations for mobile
    this.cards.forEach((card, index) => {
      gsap.fromTo(card,
        {
          opacity: 0,
          y: 40
        },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: card,
            start: 'top bottom-=100',
            end: 'top center',
            toggleActions: 'play none none reverse',
            id: `mobile-${index}`
          }
        }
      );
    });

    console.log('[FeatureStacking] Mobile animations initialized successfully');
  }

  handleResize() {
    let resizeTimer;

    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);

      resizeTimer = setTimeout(() => {
        const wasDesktop = this.isDesktop;
        this.isDesktop = window.innerWidth >= 1024;

        // If viewport changed between mobile/desktop, reinitialize
        if (wasDesktop !== this.isDesktop) {
          console.log('[FeatureStacking] Viewport changed - reinitializing');
          this.destroy();
          this.init();
        } else {
          // Just refresh ScrollTrigger
          ScrollTrigger.refresh();
        }
      }, 250);
    });
  }

  destroy() {
    console.log('[FeatureStacking] Destroying animations');

    // Kill all ScrollTrigger instances
    ScrollTrigger.getAll().forEach(trigger => {
      if (trigger.vars.id && (
        trigger.vars.id.includes('stack-') ||
        trigger.vars.id.includes('mobile-')
      )) {
        trigger.kill();
      }
    });

    // Kill all tweens on cards
    if (this.cards) {
      gsap.killTweensOf(this.cards);
      // Reset transforms
      gsap.set(this.cards, { scale: 1, rotateZ: 0, y: 0, opacity: 1 });
    }
  }
}

// ========================================
// AUTO-INITIALIZE
// ========================================
function initFeatureStacking() {
  // Check if we're on a page with the feature stacking section
  const container = document.querySelector('.features-scroll-container');

  if (!container) {
    console.log('[FeatureStacking] Container not found on this page');
    return;
  }

  // Initialize the feature stacking
  new FeatureStacking();
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initFeatureStacking);
} else {
  initFeatureStacking();
}

// Export for potential manual initialization
export default FeatureStacking;
